import { AchievementConfig, AchievementName, AchievementSeriesName } from '../types';
import {
  hasNoConsumptionData,
  hasNoActiveHvacProject,
  hasNoActiveEnergyBackupProject,
  hasNoSolarSystem,
} from './achievement-qualification-functions';

export const ACHIEVEMENTS_SERIES_MAP: Record<AchievementSeriesName, AchievementName[]> = {
  'winning-streak': ['streak-rookie', '4-week-streak', '6-week-streak', '8-week-streak', 'streak-pro'],
  'exported-energy-milestones': [
    'exported-50-kwh',
    'exported-100-kwh',
    'exported-300-kwh',
    'exported-500-kwh',
    'exported-700-kwh',
    'exported-1000-kwh',
  ],
  'trivia-streak': [
    'trivia-first-correct',
    'trivia-streak-5',
    'trivia-streak-10',
    'trivia-streak-15',
    'trivia-streak-20',
    'trivia-streak-30',
    'trivia-streak-50',
    'trivia-streak-100',
  ],
  'energy-action-streak': ['weekly-warrior', 'power-user'],
};

export const ACHIEVEMENTS_CONFIGS_MAP: Record<AchievementName, AchievementConfig> = {
  'data-pioneer': {
    type: 'cumulativeMetric',
    pointsReward: 300,
    target: {
      value: 100,
      unit: 'percent',
    },
  },
  'data-powered': {
    type: 'discrete',
    pointsReward: 500,
    qualificationFunction: hasNoConsumptionData,
  },
  'fast-learner': {
    type: 'milestone',
    pointsReward: 100,
    target: {
      value: 1,
      unit: 'weeks',
    },
  },
  'streak-rookie': {
    type: 'streak',
    pointsReward: 200,
    target: {
      value: 2,
      unit: 'weeks',
    },
  },
  '4-week-streak': {
    type: 'streak',
    pointsReward: 1000,
    target: {
      value: 4,
      unit: 'weeks',
    },
  },
  '6-week-streak': {
    type: 'streak',
    pointsReward: 3000,
    target: {
      value: 6,
      unit: 'weeks',
    },
  },
  '8-week-streak': {
    type: 'streak',
    pointsReward: 5000,
    target: {
      value: 8,
      unit: 'weeks',
    },
  },
  'streak-pro': {
    type: 'streak',
    pointsReward: 10000,
    target: {
      value: 10,
      unit: 'weeks',
    },
  },
  'exported-50-kwh': {
    type: 'milestone',
    pointsReward: 100,
    target: {
      value: 50,
      unit: 'kWh',
    },
  },
  'exported-100-kwh': {
    type: 'milestone',
    pointsReward: 100,
    target: {
      value: 100,
      unit: 'kWh',
    },
  },
  'exported-300-kwh': {
    type: 'milestone',
    pointsReward: 200,
    target: {
      value: 300,
      unit: 'kWh',
    },
  },
  'exported-500-kwh': {
    type: 'milestone',
    pointsReward: 500,
    target: {
      value: 500,
      unit: 'kWh',
    },
  },
  'exported-700-kwh': {
    type: 'milestone',
    pointsReward: 500,
    target: {
      value: 700,
      unit: 'kWh',
    },
  },
  'exported-1000-kwh': {
    type: 'milestone',
    pointsReward: 500,
    target: {
      value: 1000,
      unit: 'kWh',
    },
  },
  'weekly-warrior': {
    type: 'streak',
    pointsReward: 1000,
    target: {
      value: 4,
      unit: 'weeks',
    },
  },
  'power-user': {
    type: 'streak',
    pointsReward: 3000,
    target: {
      value: 8,
      unit: 'weeks',
    },
  },
  'trivia-first-correct': {
    type: 'streak',
    pointsReward: 100,
    target: {
      value: 1,
      unit: 'correctAnswers',
    },
  },
  'trivia-streak-5': {
    type: 'streak',
    pointsReward: 500,
    target: {
      value: 5,
      unit: 'correctAnswers',
    },
  },
  'trivia-streak-10': {
    type: 'streak',
    pointsReward: 1000,
    target: {
      value: 10,
      unit: 'correctAnswers',
    },
  },
  'trivia-streak-15': {
    type: 'streak',
    pointsReward: 1500,
    target: {
      value: 15,
      unit: 'correctAnswers',
    },
  },
  'trivia-streak-20': {
    type: 'streak',
    pointsReward: 2000,
    target: {
      value: 20,
      unit: 'correctAnswers',
    },
  },
  'trivia-streak-30': {
    type: 'streak',
    pointsReward: 3000,
    target: {
      value: 30,
      unit: 'correctAnswers',
    },
  },
  'trivia-streak-50': {
    type: 'streak',
    pointsReward: 5000,
    target: {
      value: 50,
      unit: 'correctAnswers',
    },
  },
  'trivia-streak-100': {
    type: 'streak',
    pointsReward: 10000,
    target: {
      value: 100,
      unit: 'correctAnswers',
    },
  },
  'solar-pro': {
    type: 'discrete',
    pointsReward: 0,
    couponReward: 1500,
    qualificationFunction: hasNoSolarSystem,
    unachievedCta: {
      labelLocale: 'badge.cta.startQuest',
      destination: {
        to: '/energy',
        search: { openTask: 'solar-quest' },
      },
    },
    achievedCta: {
      labelLocale: 'badge.cta.shopSolar',
      destination: {
        to: '/shop/products/$category',
        params: { category: '699cbe3e2fd721cd70f7311d' },
      },
    },
  },
  'hvac-expert': {
    type: 'discrete',
    pointsReward: 0,
    couponReward: 500,
    qualificationFunction: hasNoActiveHvacProject,
    unachievedCta: {
      labelLocale: 'badge.cta.startQuest',
      destination: {
        to: '/energy',
        search: { openTask: 'hvac-quest' },
      },
    },
    achievedCta: {
      labelLocale: 'badge.cta.shopHvac',
      destination: {
        to: '/shop/products/$category',
        params: { category: '699dbe822fd721cd70f73121' },
      },
    },
  },
  'battery-savvy': {
    type: 'discrete',
    pointsReward: 0,
    couponReward: 500,
    qualificationFunction: hasNoActiveEnergyBackupProject,
    unachievedCta: {
      labelLocale: 'badge.cta.startQuest',
      destination: {
        to: '/energy',
        search: { openTask: 'storage-quest' },
      },
    },
    achievedCta: {
      labelLocale: 'badge.cta.shopBatteries',
      destination: {
        to: '/shop/products/$category',
        params: { category: '699cbe952fd721cd70f7311f' },
      },
    },
  },
};
